import { Typography, Stack, Skeleton, Divider } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useFigures } from '@cibo/ui'

import { ProgramOpportunityDisplayProps } from './ProgramOpportunityTypes'
import { FDRStatus } from '@cibo/core'

export const CommittedProgramDisplay = ({
  program,
  isLoading,
  opportunities,
  stat,
  tokens,
}: Partial<ProgramOpportunityDisplayProps>) => {
  const { t } = useTranslation('impact/pages/ProgramOpportunity')
  const { acres } = useFigures()

  const availableOpportunities = opportunities?.filter(
    ({ status, fields }) => status === FDRStatus.eligibleForEnrollment && !!fields
  )
  const estimatedRewards = opportunities?.filter(
    ({ rewards, status }) => status === FDRStatus.enrolled && !!rewards?.opportunities
  )

  return (
    <Stack divider={<Divider />} spacing={1}>
      <Stack direction="row" spacing={2}>
        <Stack flex={1}>
          <Typography color="text.secondary" variant="overline">
            {t('enrolled')}
          </Typography>
          <Typography color="text.secondary" data-testid={`field-count_${program?.programId}`}>
            {isLoading || !stat ? <Skeleton /> : t('fields', { count: stat.committed.fields })}{' '}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {isLoading || !stat ? <Skeleton /> : acres(stat.committed.acres)}
          </Typography>
        </Stack>
        <Stack flex={1}>
          <Typography color="text.secondary" variant="overline">
            {t('estRewards')}
          </Typography>
          {estimatedRewards?.map((opportunity, i) => (
            <Stack key={`${opportunity.programId}-${i}`}>
              {opportunity.rewards?.opportunities &&
                Object.values(opportunity.rewards.opportunities).map(reward =>
                  !!reward.amount && (!tokens || tokens?.includes(reward.token)) ? (
                    <Typography key={reward.token} color="text.secondary" variant="caption">
                      {' '}
                      {t('availablePossibleEarnings', {
                        amount: reward.amount,
                        context: reward.token,
                      })}
                    </Typography>
                  ) : null
                )}
            </Stack>
          ))}
        </Stack>
      </Stack>

      {!!availableOpportunities?.length ? (
        <Stack flex={2}>
          <Typography color="text.secondary" variant="overline">
            {t('moreOpportunities')}
          </Typography>{' '}
          {availableOpportunities?.map((opportunity, i) => (
            <Stack key={`${opportunity.programId}-${i}`}>
              <Typography color="text.secondary">
                {t('fields', {
                  count: opportunity.fields,
                })}
              </Typography>
              {opportunity.rewards?.opportunities &&
                Object.values(opportunity.rewards?.opportunities).map(reward =>
                  !!reward.amount && (!tokens || tokens?.includes(reward.token)) ? (
                    <Typography key={reward.token} color="text.secondary" variant="caption">
                      {t('availablePossibleEarnings', {
                        amount: reward.amount,
                        context: reward.token,
                      })}
                    </Typography>
                  ) : null
                )}
            </Stack>
          ))}
        </Stack>
      ) : (
        <Stack>
          <Typography variant="body2" color="text.secondary">
            {isLoading ? <Skeleton /> : program?.content.fields.shortDescription}
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}
